import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'next-themes';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { CookieConsent } from '@/components/CookieConsent';
import { LanguageSuggestionBanner } from '@/components/LanguageSuggestionBanner';
import { LeadMagnetPopup } from '@/components/LeadMagnetPopup';
import { ExitIntentPopup } from '@/components/ExitIntentPopup';

import i18n, { restoreSavedLanguage } from '@/config/i18n';
import { getI18nForLang, isLangSkippablePath, urlLangOf } from '@/components/LangWrapper';
import { useTranslation } from 'react-i18next';
import HreflangTags from '@/components/HreflangTags';
import RouteTracker from '@/components/RouteTracker';
import HostBranding, { isBrandedHost } from '@/components/HostBranding';
import BrandedHostGuard from '@/components/BrandedHostGuard';
import { AuthProvider } from '@/hooks/useAuth';
import { UserPreferencesProvider } from '@/hooks/useUserPreferences';
import { SchemaScript, generateOrganizationSchema, generateWebSiteSchema } from '@/lib/seoHelpers';
// The global cinematic shell (below) themes every marketing route — the
// stylesheet must load app-wide, not just on pages that import it themselves.
import '@/pages/cinematic.css';

const queryClient = new QueryClient();

export default function RootLayout() {
  const { i18n: i18nInstance } = useTranslation();
  const location = useLocation();

  // SSG renders routes CONCURRENTLY against ONE i18n singleton. Prefixed
  // routes are protected by LangWrapper's per-language clones, but the
  // unprefixed EN pages read the singleton — whichever language a sibling
  // render set last won, which is how the production /pricing page shipped
  // with 100% Polish copy under lang="en". During SSR, bind the provider to
  // the URL's language clone instead (skip app/protected paths — their
  // language comes from the user preference mutating the singleton). On the
  // client nothing changes: one render at a time, no race, and the saved-
  // language restore keeps working through the singleton.
  const routedI18n =
    import.meta.env.SSR && !isLangSkippablePath(location.pathname)
      ? getI18nForLang(urlLangOf(location.pathname))
      : i18n;

  // Restore saved language after hydration to avoid mismatch
  useEffect(() => {
    restoreSavedLanguage();
  }, []);

  // Reveal the page once the SPA has mounted + routed. On a branded host
  // index.html hides <body> (.branded-boot) to avoid flashing the pre-rendered
  // marketing landing before the guard redirects to /auth or /dashboard.
  useEffect(() => {
    document.documentElement.classList.remove('branded-boot');
  }, []);

  // Keep <html dir> in sync with current language (lang is set by HreflangTags via Helmet)
  useEffect(() => {
    document.documentElement.dir = i18nInstance.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18nInstance.language]);

  // ── Global cinematic scope ─────────────────────────────────────────────
  // Every marketing/content route renders inside the .todai-cinematic brand
  // theme (graphite + violet — the wrapper re-points the shadcn AND the
  // editorial --color-* tokens, so legacy bd-*/pelco-* pages restyle without
  // JSX changes). App surfaces keep their own systems: the designer,
  // dashboard, client portal and admin are excluded, and white-label branded
  // hosts keep their light editorial look. Pages that already carry their own
  // .todai-cinematic root (homepage, marketplace) nest harmlessly.
  const APP_SURFACE_PREFIXES = [
    '/cctv-design', '/dashboard', '/p/', '/admin', '/organization',
    '/settings', '/accept-invite', '/demo', '/try',
  ];
  const isAppSurface = APP_SURFACE_PREFIXES.some(
    (p) => location.pathname === p.replace(/\/$/, '') || location.pathname.startsWith(p),
  );
  const cinematicShell =
    !isAppSurface && !(typeof window !== 'undefined' && isBrandedHost());

  return (
    <ThemeProvider attribute="class" defaultTheme={typeof window !== 'undefined' && isBrandedHost() ? 'light' : 'dark'} storageKey="todaidesign_theme" enableSystem={false}>
      <I18nextProvider i18n={routedI18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <UserPreferencesProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <HreflangTags />
                <SchemaScript schema={generateOrganizationSchema()} />
                <SchemaScript schema={generateWebSiteSchema()} />
                <RouteTracker />
                <HostBranding />
                <LanguageSuggestionBanner />
                <Suspense fallback={<div className="min-h-screen" />}>
                  <BrandedHostGuard>
                    <div className={cinematicShell ? 'todai-cinematic min-h-screen' : undefined}>
                      <Outlet />
                    </div>
                  </BrandedHostGuard>
                </Suspense>
                <CookieConsent />
                {/* Owned-channel lead capture — marketing only. Each popup
                    self-gates (anonymous + session + route blacklist); the
                    branded-host guard keeps them off white-label client portals.
                    They render null until triggered, so SSR output is unchanged. */}
                {typeof window !== 'undefined' && !isBrandedHost() && (
                  <>
                    <LeadMagnetPopup />
                    <ExitIntentPopup />
                  </>
                )}
              </TooltipProvider>
            </UserPreferencesProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
